/**
 * Quali azioni mostrare nella chat per il numero attivo.
 *
 * Funzione pura: riceve il tipo di numero (service_name di api_configurations), il motore
 * WAHA se noto e le funzioni che WAHA ha gia' rifiutato (501) durante la sessione.
 *
 * Fonti (verificate il 26/09/2026):
 * - WAHA, matrice per motore: https://waha.devlike.pro/docs/how-to/engines/
 *   Colonne WEBJS | WPP | GOWS | NOWEB.
 *   - Elimina / Modifica messaggio: tutti i motori.
 *   - Fissa / Sblocca messaggio (POST .../messages/{id}/pin|unpin): WEBJS e NOWEB, non GOWS/WPP.
 *   - Stella (PUT /api/star): WEBJS, WPP e NOWEB, non GOWS.
 *   - Inoltra (POST /api/forwardMessage): WEBJS, WPP e NOWEB, non GOWS.
 *   - Reazioni, posizione, contatto, sondaggio: tutti i motori.
 * - WhatsApp Cloud API (Meta): non esistono modifica, elimina, inoltra, fissa, stella e
 *   sondaggi. Reazioni, posizione e contatto esistono (li rende funzionanti lo store).
 */

export type ChatServiceName = 'waha' | 'whatsapp_cloud' | 'whatsapp_cloud_coex' | string | null | undefined;
export type WahaEngine = 'NOWEB' | 'WEBJS' | 'GOWS' | 'WPP' | string | null | undefined;

/** Nomi usati da WahaApiService.markUnsupported() dopo un 501. */
export type RuntimeFeature = 'editMessage' | 'deleteMessage' | 'pinMessage' | 'unpinMessage' | 'forwardMessage' | 'starMessage';

export interface ChatCapabilities {
  isCloud: boolean;
  canReply: boolean;
  canReact: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canForward: boolean;
  canPin: boolean;
  canStar: boolean;
  canSendPoll: boolean;
  canSendLocation: boolean;
  canSendContact: boolean;
}

export interface ChatCapabilitiesInput {
  serviceName: ChatServiceName;
  engine?: WahaEngine;
  /** Funzioni che WAHA ha risposto 501 in questa sessione. */
  unsupported?: ReadonlySet<string> | readonly string[];
}

export function isCloudService(serviceName: ChatServiceName): boolean {
  return serviceName === 'whatsapp_cloud' || serviceName === 'whatsapp_cloud_coex';
}

// Motori che NON hanno la funzione (dalla matrice WAHA). Motore ignoto: si prova e,
// se WAHA risponde 501, la voce sparisce grazie a `unsupported`.
const PIN_MISSING = new Set(['GOWS', 'WPP']);
const STAR_MISSING = new Set(['GOWS']);
const FORWARD_MISSING = new Set(['GOWS']);

/**
 * Nome del motore WAHA in maiuscolo. WAHA in /api/sessions/{nome} manda `engine` come
 * oggetto ({ engine: 'NOWEB', ... }), non come testo: prima un .toUpperCase() su
 * quell'oggetto faceva cadere tutta la pagina all'apertura di una chat (26/09/2026).
 */
export function engineName(value: unknown): string {
  if (typeof value === 'string') return value.toUpperCase();
  if (value && typeof value === 'object') {
    const inner = (value as { engine?: unknown; name?: unknown }).engine ?? (value as { name?: unknown }).name;
    if (typeof inner === 'string') return inner.toUpperCase();
  }
  return '';
}

export function getChatCapabilities(input: ChatCapabilitiesInput): ChatCapabilities {
  if (isCloudService(input.serviceName)) {
    return {
      isCloud: true,
      canReply: true,
      canReact: true,
      canEdit: false,
      canDelete: false,
      canForward: false,
      canPin: false,
      canStar: false,
      canSendPoll: false,
      canSendLocation: true,
      canSendContact: true,
    };
  }

  const engine = engineName(input.engine);
  const off = new Set(input.unsupported ? Array.from(input.unsupported) : []);

  return {
    isCloud: false,
    canReply: true,
    canReact: true,
    canEdit: !off.has('editMessage'),
    canDelete: !off.has('deleteMessage'),
    canForward: !FORWARD_MISSING.has(engine) && !off.has('forwardMessage'),
    canPin: !PIN_MISSING.has(engine) && !off.has('pinMessage'),
    canStar: !STAR_MISSING.has(engine) && !off.has('starMessage'),
    canSendPoll: true,
    canSendLocation: true,
    canSendContact: true,
  };
}
